<script setup lang="ts">
import { BaseCard } from '@/components/Cards'
import store from '@/store'
import type { DetectionModel } from '@/types/models/DetectionModel'
import type { DeviceModel } from '@/types/models/DeviceModel'
import type { AlertSocketMessageModel } from '@/types/socket/SocketAlertModel'
import moment from 'moment'
import { computed, inject } from 'vue'
import DeviceUnavailabilityCardComponent from '../../../views/EventsView/Detail/CheckpointsTable/components/DeviceUnavailabilityCardComponent.vue'
import DeviceCardContent from './DeviceCardContent.vue'
import DeviceCardFooter from './DeviceCardFooter.vue'
import DeviceCardHeader from './DeviceCardHeader.vue'
import CurrentlyRentOverlay from './components/CurrentlyRentOverlay.vue'

type Props = {
    data: DeviceModel
    status?: AlertSocketMessageModel
    detections: DetectionModel[]
    showNextEvent?: boolean
    showNextCheckpoint?: boolean
    disableClick?: boolean
}

const props = defineProps<Props>()
type Emits = {
    (e: 'click'): void
}
defineEmits<Emits>()

const organization_id = computed(() => store.state.authentication.me.organization_id)

const currentlyRent = computed(
    () =>
        props.data?.rental?.start_at &&
        props.data?.rental?.end_at &&
        !!props.data?.rental?.renter?.organization_id &&
        props.data?.rental?.renter?.organization_id !== organization_id.value &&
        moment().isAfter(moment.utc(props.data?.rental.start_at)) &&
        moment().isBefore(moment.utc(props.data?.rental.end_at))
)

const defaultCard = inject('defaultCard', false)
</script>

<template>
    <BaseCard
        :class="`flex primary-border color-text device-card ${disableClick ? '' : 'pointer'}`"
        @click="$emit('click')"
        v-bind="$attrs">
        <template #title>
            <DeviceCardHeader
                :title="data.name ?? data.serial_number"
                :subtitle="data.device_type_name"
                :data="data"
                :status="status"
                :currentlyRent="!defaultCard && currentlyRent"
                class="device-card-header align-items-center">
                <template #iconHeader>
                    <slot name="iconHeader" />
                </template>
            </DeviceCardHeader>
        </template>
        <template #content>
            <CurrentlyRentOverlay
                v-if="currentlyRent && !defaultCard"
                :start_date="moment(data?.rental?.start_at).toDate()"
                :end_date="moment(data?.rental?.end_at).toDate()"
                :rented_by="data?.rental?.rentable"
                :renter_name="data?.rental?.renter.name">
                <DeviceUnavailabilityCardComponent :device="data" />
            </CurrentlyRentOverlay>
            <DeviceCardContent
                v-else
                :data="data"
                :status="status"
                class="device-card-content"
                :showNextEvent="showNextEvent"
                :showNextCheckpoint="showNextCheckpoint">
                <slot name="content" />
            </DeviceCardContent>
        </template>
        <template v-if="!currentlyRent || defaultCard" #footer>
            <DeviceCardFooter :latestDetections="detections" />
        </template>
    </BaseCard>
</template>

<style scoped lang="scss">
.currently-rent-device {
    background-color: var(--color-background-secondary);
}

.device-card {
    flex-direction: column;

    & :deep(.card-header-title) {
        min-height: 70px;
        & .title {
            font-size: large;
            color: var(--color-text);
        }
    }

    & :deep(.card-header-title .subtitle) {
        color: var(--color-text-secondary);
    }

    & :deep(.card-content) {
        height: 100%;
    }

    & :deep(.card-footer) {
        flex: auto;
        height: 100%;
    }

    & :deep(.card-header-title),
    & :deep(.card-content),
    & :deep(.card-footer) {
        padding: 0.5em;
    }
}
</style>
